import {formatPrice} from '../lib/format-price'
import type {JobDetailFinancialLineItem} from './types'

const CHARGE_UNIT_OPTIONS: Array<{value: string; label: string}> = [
    {value: 'each', label: 'Each'},
    {value: 'gallon', label: 'Gallon'},
    {value: 'hour', label: 'Hour'},
    {value: 'day', label: 'Day'},
    {value: 'week', label: 'Week'},
    {value: 'month', label: 'Month'},
    {value: 'load', label: 'Load'},
]

const UNIT_PLURALS: Record<string, string> = {
    each: 'each',
    gallon: 'gallons',
    hour: 'hours',
    day: 'days',
    week: 'weeks',
    month: 'months',
    load: 'loads',
    barrel: 'barrels',
    ton: 'tons',
    tote: 'totes',
    project: 'project',
}

// Legacy unit spellings
const LEGACY_UNITS: Record<string, string> = {
    hours: 'hour',
    hr: 'hour',
    days: 'day',
    weeks: 'week',
    months: 'month',
    flat: 'each',
    ea: 'each',
    gallons: 'gallon',
    gal: 'gallon',
    loads: 'load',
    totes: 'tote',
}

export function normalizeChargeUnit(unit: string): string {
    const key = unit.trim().toLowerCase()
    return LEGACY_UNITS[key] ?? key
}

export function chargeUnitOptions(currentUnit?: string): Array<{value: string; label: string}> {
    const unit = currentUnit ? normalizeChargeUnit(currentUnit) : ''
    if (!unit || CHARGE_UNIT_OPTIONS.some((option) => option.value === unit)) {
        return CHARGE_UNIT_OPTIONS
    }
    return [
        ...CHARGE_UNIT_OPTIONS,
        {value: unit, label: unit.charAt(0).toUpperCase() + unit.slice(1)},
    ]
}

function chargeUnitLabel(unit: string, quantity: number): string {
    const singular = normalizeChargeUnit(unit)
    return quantity === 1 ? singular : (UNIT_PLURALS[singular] ?? singular)
}

export function chargeTotal(quantity: number, unitPrice: number): number {
    return Math.round(quantity * unitPrice * 100) / 100
}

function formatQuantity(quantity: number): string {
    return quantity.toLocaleString('en-US', {maximumFractionDigits: 2})
}

function formatChargeRate(unitPrice: number): string {
    return formatPrice(unitPrice, {maxFractionDigits: 4})
}

export function financialLineItemRateSummary(item: {
    quantity: number
    unit: string
    unitPrice: number
}): string {
    const unit = normalizeChargeUnit(item.unit)
    if (item.quantity === 1 && (unit === 'each' || unit === 'project')) {
        return formatChargeRate(item.unitPrice)
    }
    const per = unit === 'each' ? 'each' : `per ${unit}`
    const quantity = `${formatQuantity(item.quantity)} ${chargeUnitLabel(unit, item.quantity)}`
    return `${quantity} × ${formatChargeRate(item.unitPrice)} ${per}`
}

export function financialLineItemsChanged(
    before: JobDetailFinancialLineItem[],
    after: JobDetailFinancialLineItem[],
): boolean {
    if (before.length !== after.length) {
        return true
    }

    const beforeById = new Map(before.map((item) => [item.id, item]))
    for (const afterItem of after) {
        const beforeItem = beforeById.get(afterItem.id)
        if (!beforeItem) {
            return true
        }

        if (
            beforeItem.name !== afterItem.name ||
            beforeItem.description !== afterItem.description ||
            beforeItem.quantity !== afterItem.quantity ||
            beforeItem.unit !== afterItem.unit ||
            beforeItem.unitPrice !== afterItem.unitPrice ||
            beforeItem.totalPrice !== afterItem.totalPrice ||
            beforeItem.invoiceId !== afterItem.invoiceId
        ) {
            return true
        }
    }

    return false
}
